import React, { type ReactNode, createContext, useContext, useState } from 'react';
import type { CustomerCardFormValues } from '@makemydeal/dr-dash-types';
import type { FormConfigValidationsErrors } from '@makemydeal/dr-dash-ui-utils';
import type { ContractFlowValidationEngine } from '../hooks/useFormValidationWithContractFlow';
import type { BUYER, CO_BUYER } from './constants';
import { hasAnyFieldValue } from './utils/CustomerCardUtils';

type CustomerCardContextType = {
    formValues: CustomerCardFormValues;
    initialFormValues: CustomerCardFormValues;
    customerValidator: ContractFlowValidationEngine;
    isLocked: boolean;
    customerType: typeof BUYER | typeof CO_BUYER;
    handleInputChange: (field: string, value: string) => void;
    handleNumberInputChange: (field: string, value: string) => void;
    customerFormErrors: FormConfigValidationsErrors;
    isAdditionalFieldsExpanded: boolean;
    setIsAdditionalFieldsExpanded: React.Dispatch<React.SetStateAction<boolean>>;
    isPreviousAddressExpanded: boolean;
    setIsPreviousAddressExpanded: React.Dispatch<React.SetStateAction<boolean>>;
    isPrevEmploymentFieldsExpanded: boolean;
    setIsPrevEmploymentFieldsExpanded: React.Dispatch<React.SetStateAction<boolean>>;
    hasHousingAndEmploymentData: boolean;
    hasBusinessData: boolean;
};

export const CustomerCardContext = createContext<CustomerCardContextType | undefined>(undefined);

type CustomerCardProviderProps = {
    children: ReactNode;
    initialFormValues: CustomerCardFormValues;
    formValues: CustomerCardFormValues;
    customerValidator: ContractFlowValidationEngine;
    isLocked: boolean;
    customerType: typeof BUYER | typeof CO_BUYER;
    handleInputChange: (field: string, value: string) => void;
    handleNumberInputChange: (field: string, value: string) => void;
    customerFormErrors: FormConfigValidationsErrors;
};

export const CustomerCardProvider = ({
    children,
    initialFormValues,
    formValues,
    customerValidator,
    isLocked,
    customerType,
    handleInputChange,
    handleNumberInputChange,
    customerFormErrors
}: CustomerCardProviderProps) => {
    const initialOtherIncome = initialFormValues.financialInfo?.otherIncome;
    const initialOtherIncomeSource = initialFormValues.financialInfo?.otherIncomeSource;
    const hasExistingData = Boolean(initialOtherIncome || initialOtherIncomeSource);
    const [isAdditionalFieldsExpanded, setIsAdditionalFieldsExpanded] = useState(hasExistingData);

    const initialPreviousAddress = initialFormValues.previousAddress;
    const hasExistingPreviousAddress = Boolean(
        initialPreviousAddress?.streetAddress ||
            initialPreviousAddress?.streetAddress2 ||
            initialPreviousAddress?.city ||
            initialPreviousAddress?.state ||
            initialPreviousAddress?.zip ||
            initialPreviousAddress?.county ||
            !isNaN(Number(initialFormValues.yearsAtPreviousAddress)) ||
            !isNaN(Number(initialFormValues.monthsAtPreviousAddress))
    );
    const [isPreviousAddressExpanded, setIsPreviousAddressExpanded] = useState(hasExistingPreviousAddress);

    const hasExistingPreviousEmployment = Object.values(initialFormValues.previousEmployment || {}).some(
        (value) => value !== undefined && value !== null && value !== ''
    );
    const [isPrevEmploymentFieldsExpanded, setIsPrevEmploymentFieldsExpanded] = useState(hasExistingPreviousEmployment);
    const hasHousingAndEmploymentData = hasAnyFieldValue(
        initialFormValues.housingInfo,
        initialFormValues.yearsAtCurrentAddress,
        initialFormValues.monthsAtCurrentAddress,
        initialFormValues.previousAddress,
        initialFormValues.yearsAtPreviousAddress,
        initialFormValues.monthsAtPreviousAddress,
        initialFormValues.currentEmployment,
        initialFormValues.previousEmployment,
        initialFormValues.financialInfo
    );
    const hasBusinessData = hasAnyFieldValue(
        initialFormValues.business?.bankName,
        initialFormValues.business?.bankContactName,
        initialFormValues.business?.bankContactPhoneNumber,
        initialFormValues.business?.taxIdNumber,
        initialFormValues.business?.businessEstablishedDate,
        initialFormValues.business?.numberOfEmployees,
        initialFormValues.business?.stateOfIncorporation,
        initialFormValues.business?.financialStatementDescription,
        initialFormValues.business?.businessType
    );
    return (
        <CustomerCardContext.Provider
            value={{
                formValues,
                initialFormValues,
                customerValidator,
                isLocked,
                customerType,
                handleInputChange,
                handleNumberInputChange,
                customerFormErrors,
                isAdditionalFieldsExpanded,
                setIsAdditionalFieldsExpanded,
                isPreviousAddressExpanded,
                setIsPreviousAddressExpanded,
                isPrevEmploymentFieldsExpanded,
                setIsPrevEmploymentFieldsExpanded,
                hasHousingAndEmploymentData,
                hasBusinessData
            }}
        >
            {children}
        </CustomerCardContext.Provider>
    );
};

export const useCustomerCardContext = (): CustomerCardContextType => {
    const context = useContext(CustomerCardContext);
    if (!context) {
        throw new Error('useCustomerCardContext must be used within a CustomerCardProvider');
    }
    return context;
};
